/**
 * CreateListModal Component
 *
 * Modal form for creating a new custom list.
 * Includes name (required), description (optional), and visibility picker.
 */

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { TextInput } from '@/components/ui/text-input';
import type { List } from '@/features/lists/types';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useCreateList } from '../hooks/use-list-mutations';

interface CreateListModalProps {
  visible: boolean;
  onClose: () => void;
  onCreated?: (list: List) => void;
}

type Visibility = 'private' | 'friends' | 'public';

const VISIBILITY_OPTIONS: Array<{ value: Visibility; label: string; icon: string }> = [
  { value: 'private', label: 'Private', icon: 'lock-closed-outline' },
  { value: 'friends', label: 'Friends', icon: 'people-outline' },
  { value: 'public', label: 'Public', icon: 'globe-outline' },
];

export function CreateListModal({ visible, onClose, onCreated }: CreateListModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('private');

  const createList = useCreateList();

  const handleCreate = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    createList.mutate(
      {
        name: trimmedName,
        description: description.trim() || undefined,
        visibility,
      },
      {
        onSuccess: (list) => {
          setName('');
          setDescription('');
          setVisibility('private');
          onClose();
          onCreated?.(list);
        },
      }
    );
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setVisibility('private');
    onClose();
  };

  return (
    <Modal visible={visible} onClose={handleClose}>
      <View className="bg-background rounded-xl">
        <Modal.Header>
          <View className="flex-row items-center justify-between">
            <Text className="text-lg font-bold text-foreground">
              Create List
            </Text>
            <Pressable
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Ionicons name="close" size={24} className="text-muted-foreground" />
            </Pressable>
          </View>
        </Modal.Header>

        <Modal.Content>
          <TextInput
            label="Name"
            placeholder="e.g. Best Pizza in Town"
            value={name}
            onChangeText={setName}
            maxLength={100}
            autoFocus
          />

          <View style={{ marginTop: 16 }}>
            <TextInput
              label="Description (optional)"
              placeholder="What is this list about?"
              value={description}
              onChangeText={setDescription}
              maxLength={500}
              multiline
              multilineMinHeight={60}
            />
          </View>

          <View style={{ marginTop: 16 }}>
            <Text className="text-sm font-semibold mb-1.5 px-1 text-muted-foreground">
              Visibility
            </Text>
            <View className="flex-row gap-2">
              {VISIBILITY_OPTIONS.map((option) => (
                <Pressable
                  key={option.value}
                  onPress={() => setVisibility(option.value)}
                  className={`flex-1 flex-row items-center justify-center p-3 rounded-lg border ${
                    visibility === option.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-surface'
                  }`}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: visibility === option.value }}
                  accessibilityLabel={option.label}
                >
                  <Ionicons
                    name={option.icon as any}
                    size={16}
                    className={visibility === option.value ? 'text-primary' : 'text-muted-foreground'}
                  />
                  <Text
                    className={`ml-1.5 text-sm font-medium ${
                      visibility === option.value ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        </Modal.Content>

        <Modal.Footer>
          <Button variant="ghost" onPress={handleClose}>
            Cancel
          </Button>
          <Button
            onPress={handleCreate}
            disabled={!name.trim()}
            loading={createList.isPending}
          >
            Create
          </Button>
        </Modal.Footer>
      </View>
    </Modal>
  );
}
